// src/services/bookmark.js

// Base API URL - should match provider.js
const API_BASE_URL = `${import.meta.env.VITE_API_URL}/api`;
const API_KEY = import.meta.env.VITE_API_KEY

/**
 * Fetch all bookmarks for the current user
 * @param {string} token - JWT authentication token
 * @param {number} page - Page number (default: 1)
 * @param {number} pageSize - Number of items per page (default: 10)
 * @returns {Promise<Object>} - Object containing bookmarks array and pagination data
 */
export async function fetchBookmarks(token, page = 1, pageSize = 10) {
  const params = new URLSearchParams();
  params.append('page', page.toString());
  params.append('page_size', pageSize.toString());
  
  const response = await fetch(`${API_BASE_URL}/bookmarks?${params.toString()}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY
    }
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  const data = await response.json();
  
  // Return structured response matching backend format
  return {
    bookmarks: data.bookmarks || [],
    pagination: {
      total: data.pagination?.total || 0,
      page: data.pagination?.page || 1,
      page_size: data.pagination?.page_size || 10,
      total_pages: data.pagination?.total_pages || 1
    }
  };
}

/**
 * Create a new bookmark for a provider
 * @param {string} token - JWT authentication token
 * @param {string} providerId - ID of the provider to bookmark
 * @returns {Promise<Object>} - Created bookmark object
 */
export async function createBookmark(token, providerId) {
  const response = await fetch(`${API_BASE_URL}/bookmarks`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
        'X-API-Key': API_KEY
    },
    body: JSON.stringify({
      provider_id: providerId
    })
  });
  
  if (!response.ok) {
    // Handle specific error cases like duplicate bookmarks
    if (response.status === 400) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create bookmark');
    }
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return await response.json();
}

/**
 * Delete a bookmark for a provider
 * @param {string} token - JWT authentication token
 * @param {string} providerId - ID of the provider to remove from bookmarks
 * @returns {Promise<void>}
 */
export async function deleteBookmark(token, providerId) {
  const response = await fetch(`${API_BASE_URL}/bookmarks/${providerId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
        'X-API-Key': API_KEY
    }
  });
  
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Bookmark not found');
    }
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  // DELETE typically returns no content
  return;
}

/**
 * Check if a provider is bookmarked by the current user
 * @param {string} token - JWT authentication token
 * @param {string} providerId - ID of the provider to check
 * @returns {Promise<boolean>} - True if bookmarked, false otherwise
 */
export async function isProviderBookmarked(token, providerId) {
  try {
    const response = await fetch(`${API_BASE_URL}/bookmarks/check/${providerId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'X-API-Key': API_KEY
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data.is_bookmarked;
  } catch (error) {
    console.error('Error checking bookmark status:', error);
    return false;
  }
}

/**
 * Toggle bookmark status for a provider
 * @param {string} token - JWT authentication token
 * @param {string} providerId - ID of the provider
 * @param {boolean} isCurrentlyBookmarked - Current bookmark status
 * @returns {Promise<boolean>} - New bookmark status
 */
export async function toggleBookmark(token, providerId, isCurrentlyBookmarked) {
  if (isCurrentlyBookmarked) {
    await deleteBookmark(token, providerId);
    return false;
  } else {
    await createBookmark(token, providerId);
    return true;
  }
}